import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import classNames from 'classnames';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { SizeCodeForBooking, SizeCodeType } from 'entities/SizeCode';
import { RentOptionDocument } from 'entities/RentOption';
import { Box } from 'entities/Box';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getLoggedUserData } from 'entities/User';
import { getBoxInfo, getWarehouseInfo } from 'pages/BookingPage';
import { WarehouseMap } from 'widgets/SelectBoxSizeForm/ui/WarehouseMap';
import { useGetFloorsByWarehouseIdQuery } from 'entities/Floor';
import { Button } from 'shared/ui/Button';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';
import { ModernBoxesContainer } from './ModernBoxesContainer';
import { ModernBoxCard } from './ModernBoxCard';
import s from './BoxSelectionModal.module.scss';

interface BoxSelectionModalProps {
  isOpen: boolean;
  sizeCode: SizeCodeForBooking;
  rentOptions: RentOptionDocument[];
  selectedBoxId: string | undefined;
  onClose: () => void;
  onSelectBox: (box: Box) => void;
  onBookBox: (box: Box) => void;
}

export const BoxSelectionModal: FC<BoxSelectionModalProps> = memo((props) => {
  const { isOpen, sizeCode, rentOptions, selectedBoxId, onClose, onSelectBox, onBookBox } = props;
  const { square, sizeCodeType, sizeCodeId } = sizeCode;

  const { t } = useAppTranslation('booking');
  const loggedUser = useAppSelector(getLoggedUserData);
  const warehouseInfo = useAppSelector(getWarehouseInfo);
  const selectedBox = useAppSelector(getBoxInfo);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const isListView = viewMode === 'list';
  const isMapView = viewMode === 'map';
  const { data: floors } = useGetFloorsByWarehouseIdQuery(warehouseInfo?.warehouseId, { skip: !warehouseInfo?.warehouseId });
  const hasMap = Boolean(floors?.some((floor) => floor.shapesConfig || floor.floorSchemaBackgroundUrl));

  // Filter discounts for this size code (based on original SizeCodeCollapse logic)
  const rentDiscounts = useMemo(() => {
    return rentOptions.filter((option) => option.unitSizes?.includes(square.toString())) || [];
  }, [rentOptions, square]);

  const generalDiscounts = useMemo(() => {
    return rentOptions.filter((option) => !option.unitSizes && option.showAsPromotion) || [];
  }, [rentOptions]);

  useEffect(() => {
    if (!hasMap && viewMode === 'map') {
      setViewMode('list');
    }
  }, [hasMap, viewMode]);

  const switchToList = useCallback(() => {
    setViewMode('list');
  }, []);

  const switchToMap = useCallback(() => {
    if (hasMap) {
      setViewMode('map');
    }
  }, [hasMap]);

  const handleMapSelectBox = useCallback(
    (box: Box) => {
      onSelectBox(box);
    },
    [onSelectBox],
  );

  const modalTitle = (
    <div className="font-semibold pb-2 text-2xl font-tektur">{`${t('Boxes')} ${formatDecimal(square)} м${
      sizeCodeType === SizeCodeType.CUBIC_BOX ? '³' : '²'
    }`}</div>
  );

  return (
    <Modal
      title={modalTitle}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={800}
      centered
      destroyOnClose
      className={classNames(s.modalBg, s.modalScrollable)}
    >
      <div className="flex flex-col h-full min-h-0">
        <div className="flex-1 min-h-0">
          {viewMode === 'list' ? (
            <div className="h-full overflow-y-auto px-3 pb-3">
              <ModernBoxesContainer
                sizeCodeId={sizeCodeId}
                selectedBoxId={selectedBoxId}
                rentDiscounts={rentDiscounts}
                generalDiscounts={generalDiscounts}
                onSelectBox={onSelectBox}
                onBookBox={onBookBox}
                accountType={loggedUser?.accountType}
                warehouseInfo={warehouseInfo}
              />
            </div>
          ) : (
            <div className="h-full flex flex-col min-h-0">
              <div className="flex-1 min-h-0">
                {warehouseInfo?.warehouseId ? (
                  <WarehouseMap
                    selectedWarehouseId={warehouseInfo.warehouseId}
                    selectedBoxId={selectedBoxId}
                    allowedSizeCodeId={sizeCodeId}
                    preferredFloorId={selectedBox?.floor?.floorId}
                    containerClassName="h-full"
                    centerOnSelected
                    onSelectBox={(box, _sizeCodeId) => {
                      handleMapSelectBox(box);
                    }}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center text-secondaryLight">{t('Warehouse not selected')}</div>
                )}
              </div>
              {selectedBox && (
                <div className="mt-3">
                  <ModernBoxCard
                    box={selectedBox}
                    selectedBoxId={selectedBoxId}
                    hasRentDiscounts={rentDiscounts.length > 0}
                    hasGeneralDiscounts={generalDiscounts.filter(({ showAsPromotion }) => showAsPromotion).length > 0}
                    onSelectBox={onSelectBox}
                    onBookBox={onBookBox}
                    accountType={loggedUser?.accountType}
                    warehouseInfo={warehouseInfo}
                  />
                </div>
              )}
              <div className="flex justify-center py-3 gap-2">
                <Button theme={isListView ? 'primary' : 'secondary'} size="small" fontSize="small" onClick={switchToList}>
                  {t('List')}
                </Button>
                <Button theme={isMapView ? 'primary' : 'secondary'} size="small" fontSize="small" onClick={switchToMap}>
                  {t('Map')}
                </Button>
              </div>
            </div>
          )}
        </div>
        {isListView && (
          <div className="flex justify-center py-3 gap-2">
            <Button theme={isListView ? 'primary' : 'secondary'} size="small" fontSize="small" onClick={switchToList}>
              {t('List')}
            </Button>
            <Button theme={isMapView ? 'primary' : 'secondary'} size="small" fontSize="small" onClick={switchToMap} isDisabled={!hasMap}>
              {t('Map')}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
});
